import { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, Button } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { AnimatedBox } from '../../components/ui';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;


// Styled Components
const HeroWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'accentColor' })(({ theme, accentColor }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  position: 'relative',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(10),

  [theme.breakpoints.down('md')]: {
    paddingBottom: theme.spacing(5),
  },
  background: `
    radial-gradient(120% 90% at 85% 5%, ${accentColor}1F 0%, transparent 55%),
    radial-gradient(90% 80% at 5% 95%, ${accentColor}16 0%, transparent 60%),
    linear-gradient(180deg, #FCFDFE 0%, #F5F7FA 100%)
  `,
  overflow: 'hidden',
  transition: 'background 0.6s ease',
}));

const BackgroundDecor = styled(Box)(({ color, size, top, left, right, bottom }) => ({
  position: 'absolute',
  width: size || '400px',
  height: size || '400px',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color}1A 0%, transparent 70%)`,
  filter: 'blur(6px)',
  top,
  left,
  right,
  bottom,
  pointerEvents: 'none',
  transition: 'background 0.6s ease',
}));

// All slides share one grid cell, so the container is always as tall as the
// longest slide and the controls below it never shift between slides.
const SlideContainer = styled(Box)({
  position: 'relative',
  display: 'grid',
  width: '100%',
  alignItems: 'center',
});

const Slide = styled(Box, { shouldForwardProp: (prop) => prop !== 'isActive' })(({ isActive }) => ({
  gridArea: '1 / 1',
  width: '100%',
  opacity: isActive ? 1 : 0,
  visibility: isActive ? 'visible' : 'hidden',
  pointerEvents: isActive ? 'auto' : 'none',
  transition: 'opacity 0.5s ease-in-out, visibility 0.5s ease-in-out',
  animation: isActive ? `${fadeIn} 0.5s ease-out` : 'none',
}));

// Desktop keeps copy left and visual right; mobile leads with the visual, then
// the headline and supporting copy.
const SlideContent = styled(Box, { shouldForwardProp: (prop) => prop !== 'hasVisual' })(({ theme, hasVisual }) => ({
  display: 'grid',
  alignItems: 'center',
  columnGap: theme.spacing(6),
  rowGap: theme.spacing(2),
  padding: theme.spacing(4, 0),
  gridTemplateColumns: hasVisual ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr)',
  gridTemplateAreas: hasVisual
    ? '"headline visual" "body visual"'
    : '"headline" "body"',

  [theme.breakpoints.down('md')]: {
    columnGap: theme.spacing(4),
    padding: theme.spacing(3, 0),
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gridTemplateAreas: hasVisual
      ? '"visual" "headline" "body"'
      : '"headline" "body"',
    rowGap: theme.spacing(3),
    padding: theme.spacing(2, 0),
    justifyItems: 'stretch',
  },
}));

const HeadlineArea = styled(Box)({
  gridArea: 'headline',
  minWidth: 0,
  // Keeps the measure readable on slides with no visual to fill the right side.
  maxWidth: '780px',
});

const BodyArea = styled(Box)({
  gridArea: 'body',
  minWidth: 0,
  maxWidth: '780px',
});

// Brand marks sit directly on the background, in a fixed-size slot so the visual
// never jumps position or size as you move through the carousel.
const IconArea = styled(Box)(({ theme }) => ({
  gridArea: 'visual',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '400px',
  height: '300px',
  padding: theme.spacing(2),

  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
  },

  [theme.breakpoints.down('lg')]: {
    width: '320px',
    height: '250px',
  },

  [theme.breakpoints.down('sm')]: {
    justifySelf: 'center',
    width: '100%',
    maxWidth: '280px',
    height: '170px',
    padding: theme.spacing(1),
  },
}));

const DotsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(3),
    gap: theme.spacing(0.75),
  },
}));

// Manual navigation only: a wide pill marks where you are, and every segment is a
// target with enough padding to hit comfortably.
const SlideTrack = styled('button', {
  shouldForwardProp: (prop) => !['isActive', 'accentColor'].includes(prop),
})(({ isActive, accentColor }) => ({
  position: 'relative',
  width: isActive ? '44px' : '22px',
  height: '6px',
  padding: 0,
  border: 'none',
  borderRadius: '999px',
  cursor: 'pointer',
  background: isActive ? accentColor : 'rgba(26, 41, 66, 0.16)',
  transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease',

  '&:hover': {
    background: isActive ? accentColor : 'rgba(26, 41, 66, 0.32)',
  },

  '&:focus-visible': {
    outline: `2px solid ${accentColor}`,
    outlineOffset: '4px',
  },
}));

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !['gradient', 'accentColor'].includes(prop),
})(({ gradient, accentColor }) => ({
  background: gradient || 'linear-gradient(135deg, #00D4FF 0%, #5B8DEF 100%)',
  color: '#FFFFFF',
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: 700,
  padding: '13px 28px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.9375rem',
  letterSpacing: '0.01em',
  boxShadow: `0 8px 24px -8px ${accentColor || '#00B8D4'}A6`,
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 14px 32px -10px ${accentColor || '#00B8D4'}CC`,
  },

  '&:active': {
    transform: 'translateY(0)',
  },
}));

const OutlinedButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'accentColor' })(({ accentColor }) => ({
  borderColor: `${accentColor}4D` || 'rgba(0, 212, 255, 0.3)',
  color: accentColor || '#00D4FF',
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: 700,
  padding: '13px 28px',
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '0.9375rem',
  letterSpacing: '0.01em',
  borderWidth: '1.5px',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  transition: 'transform 0.25s ease, background-color 0.25s ease, border-color 0.25s ease',

  '&:hover': {
    backgroundColor: `${accentColor}12`,
    borderColor: accentColor || '#00D4FF',
    borderWidth: '1.5px',
    transform: 'translateY(-2px)',
  },

  '&:active': {
    transform: 'translateY(0)',
  },
}));

// Slides Data
const slides = [
  {
    key: 'nexfah',
    taglineKey: 'hero.tagline',
    titleKey: 'hero.title',
    highlightKey: 'hero.titleHighlight',
    subtitleKey: 'hero.subtitle',
    ctaUrl: '/contact',
    ctaKey: 'hero.cta.primary',
    ctaIsInternal: true,
    secondaryUrl: '/services',
    secondaryKey: 'hero.cta.secondary',
    secondaryIsInternal: true,
    accentColor: '#00B8D4',
    gradient: 'linear-gradient(135deg, #00D4FF 0%, #5B8DEF 100%)',
  },
  {
    key: 'deenmatches',
    tagline: 'Faith-Based Matrimonial Platform',
    title: 'Find Your Perfect',
    highlight: 'Life Partner',
    subtitle: 'A secure and private matrimonial platform connecting Muslim singles worldwide. Built with Islamic values, verified profiles, and advanced privacy controls.',
    ctaUrl: 'https://deenmatches.co.uk',
    ctaText: 'Explore DeenMatches',
    ctaIsInternal: false,
    accentColor: '#9b1c1c',
    gradient: 'linear-gradient(135deg, #9b1c1c 0%, #b7950b 100%)',
    iconSrc: '/images/products/deenmatches-icon.png',
  },
  {
    key: 'ubcconnect',
    tagline: 'United Business Chamber',
    title: 'Connect, Grow &',
    highlight: 'Thrive Together',
    subtitle: 'UBC Connect is the digital platform for the United Business Chamber — empowering local businesses through seamless networking, member directories, and community-driven events.',
    ctaUrl: 'https://ubc-connect.co.uk',
    ctaText: 'Explore UBC Connect',
    ctaIsInternal: false,
    accentColor: '#1a56db',
    gradient: 'linear-gradient(135deg, #1a56db 0%, #1e40af 100%)',
    iconSrc: '/images/products/ubc-connect-icon.png',
  },
  {
    key: 'suvaifoods',
    tagline: 'Taste of India',
    title: 'Order Authentic Indian Food',
    highlight: 'Delivered to You',
    subtitle: 'Suvai Foods UK brings the rich flavours of authentic Indian cuisine to your doorstep. Browse the menu, place your order, and enjoy a seamless food ordering experience.',
    ctaUrl: 'https://suvaifoods.co.uk/',
    ctaText: 'Order Now',
    ctaIsInternal: false,
    accentColor: '#e11d24',
    gradient: 'linear-gradient(135deg, #e11d24 0%, #991b1b 100%)',
    iconSrc: '/images/products/suvai-foods-icon.png',
  },
  {
    key: 'watfordmosque',
    tagline: 'Islamic Centre & Community',
    title: 'Serving the',
    highlight: 'Watford Muslim Community',
    subtitle: 'The digital home of Watford Mosque Association — your source for daily prayer times, community events, announcements, and a welcoming space for all.',
    ctaUrl: 'https://watfordmosque.nexfah.com',
    ctaText: 'Visit Website',
    ctaIsInternal: false,
    accentColor: '#16a34a',
    gradient: 'linear-gradient(135deg, #16a34a 0%, #166534 100%)',
    iconSrc: '/images/products/watford-mosque-icon.png',
  },
  {
    key: 'bookmoteasy',
    tagline: 'Workshop Management Solution',
    title: 'Streamline Your Auto',
    highlight: 'Workshop Operations',
    subtitle: 'Complete workshop management system for auto repair businesses. Online booking, job tracking, inventory management, and customer portal - all in one platform.',
    ctaUrl: 'https://bookmoteasy.co.uk',
    ctaText: 'Explore BookMotEasy',
    ctaIsInternal: false,
    accentColor: '#f97316',
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    iconSrc: '/images/products/bookmoteasy-icon.png',
  },
  {
    key: 'customwebsites',
    tagline: 'Custom Website Design & Build',
    title: 'Websites Built Around',
    highlight: 'Your Business',
    subtitle: 'From immigration advisers to healthcare clinics, we design and build professional, fast, and fully managed websites tailored to each client. Explore some of our recent work.',
    ctaUrl: 'https://arimmigrationservices.co.uk/',
    ctaText: 'AR Immigration Services',
    ctaIsInternal: false,
    secondaryUrl: 'https://birminghamsleepcentre.co.uk/',
    secondaryText: 'Birmingham Sleep Centre',
    secondaryIsInternal: false,
    accentColor: '#7c3aed',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
  },
  {
    key: 'crux360',
    tagline: 'AI-Powered Business Intelligence',
    title: 'Transform Data Into',
    highlight: 'Actionable Insights',
    subtitle: 'Advanced AI analytics platform that helps businesses make smarter decisions. Predictive insights, custom dashboards, and seamless data integration.',
    ctaUrl: 'https://crux360.ai',
    ctaText: 'Explore Crux360 AI',
    ctaIsInternal: false,
    accentColor: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    iconSrc: '/images/products/crux360-icon.png',
  },
];

export default function HeroSection() {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(3);

  const touchRef = useRef({ startX: 0, startY: 0 });

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  const handleTouchStart = useCallback((e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const deltaX = e.changedTouches[0].clientX - touchRef.current.startX;
    const deltaY = e.changedTouches[0].clientY - touchRef.current.startY;

    // Only trigger swipe if horizontal movement is greater than vertical (to avoid interfering with scroll)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        goToNextSlide();
      } else {
        goToPrevSlide();
      }
    }
  }, [goToNextSlide, goToPrevSlide]);

  const currentAccentColor = slides[currentSlide].accentColor;

  return (
    <HeroWrapper accentColor={currentAccentColor}>
      {/* Background decorations */}
      <BackgroundDecor color={currentAccentColor} size="560px" top="-18%" right="-8%" />
      <BackgroundDecor color={currentAccentColor} size="420px" bottom="-12%" left="-8%" />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 4, md: 6 } }}>
        <SlideContainer
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {slides.map((slide, index) => (
            <Slide key={slide.key} isActive={currentSlide === index}>
              <SlideContent hasVisual={slide.iconSrc ? 1 : 0}>
                <HeadlineArea>
                  <Typography
                    variant="h1"
                    sx={{
                      color: '#0a0a0a',
                      mb: 0,
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem', lg: '4rem' },
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {slide.titleKey ? t(slide.titleKey) : slide.title}{' '}
                    <Box
                      component="span"
                      sx={{
                        background: slide.gradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {slide.highlightKey ? t(slide.highlightKey) : slide.highlight}
                    </Box>
                  </Typography>
                </HeadlineArea>

                <BodyArea>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(10, 10, 10, 0.6)',
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                      lineHeight: 1.7,
                      mb: 4,
                      maxWidth: '600px',
                    }}
                  >
                    {slide.subtitleKey ? t(slide.subtitleKey) : slide.subtitle}
                  </Typography>

                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                  }}>
                    {slide.ctaIsInternal ? (
                      <StyledButton
                        component={Link}
                        to={slide.ctaUrl}
                        gradient={slide.gradient}
                        accentColor={slide.accentColor}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {slide.ctaKey ? t(slide.ctaKey) : slide.ctaText}
                      </StyledButton>
                    ) : (
                      <StyledButton
                        component="a"
                        href={slide.ctaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        gradient={slide.gradient}
                        accentColor={slide.accentColor}
                        endIcon={<ArrowForwardIcon />}
                      >
                        {slide.ctaText}
                      </StyledButton>
                    )}

                    {slide.secondaryUrl && (
                      slide.secondaryIsInternal ? (
                        <OutlinedButton
                          component={Link}
                          to={slide.secondaryUrl}
                          variant="outlined"
                          accentColor={slide.accentColor}
                        >
                          {t(slide.secondaryKey)}
                        </OutlinedButton>
                      ) : (
                        <OutlinedButton
                          component="a"
                          href={slide.secondaryUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          accentColor={slide.accentColor}
                        >
                          {slide.secondaryText}
                        </OutlinedButton>
                      )
                    )}
                  </Box>
                </BodyArea>

                {/* Slide visual: the product's brand mark */}
                {slide.iconSrc && (
                  <IconArea>
                    <img src={slide.iconSrc} alt="" loading="lazy" />
                  </IconArea>
                )}
              </SlideContent>
            </Slide>
          ))}
        </SlideContainer>

        <DotsContainer>
          {slides.map((slide, index) => (
            <SlideTrack
              key={slide.key}
              type="button"
              isActive={currentSlide === index}
              accentColor={currentAccentColor}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={currentSlide === index ? 'true' : undefined}
            />
          ))}
        </DotsContainer>

      </Container>
    </HeroWrapper>
  );
}
